import { html, css, LitElement, unsafeCSS } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import olStylesText from 'ol/ol.css?inline';

export class OSMMap extends LitElement {
    static styles = [
        unsafeCSS(olStylesText),
        css`
            *, ::after, ::before {
                box-sizing: border-box;
            }
            :host { 
                display: block; 
            }
            .ol-full-screen button {
                cursor: pointer;
            }
            .map {
                width: 100%;
                background: #eee;
                position: relative;
            }
            .tooltip {
                position: absolute;
                background-color: white;
                padding: 5px;
                border: 1px solid #ccc;
                border-radius: 4px;
                pointer-events: none;
                display: none;
            }
            .ol-popup, .map, .ol-viewport {
                transition: none !important;
            }
            .ol-popup {
                position: absolute;
                background-color: white;
                padding: 15px;
                border-radius: 10px;
                border: 1px solid #fff;
                bottom: 12px;
                left: -50px;
                min-width: 232px;
                font-weight: 300;
                font-size: 13px;
                filter: drop-shadow(0 1px 4px rgba(0,0,0,0.2));
            }
            .ol-popup::before,
            .ol-popup::after {
                border-top: 10px solid white;
                border-right: 10px solid transparent;
                border-left: 10px solid transparent;
                content: "";
                height: 0;
                width: 0;
                position: absolute;
                bottom: -9px;
                left: 40px;
            }
            .ol-popup-closer { 
                text-decoration: none; 
                position: absolute; 
                top: 2px; 
                right: 8px; 
                color: #333; 
            }
            .title {
                font-size: 16px;
                font-weight: bold;
                margin-bottom: 8px;
                line-height: 1rem;
            }
            .infowindow {
                max-height: 400px;
                overflow-y: auto;
            }
            .infowindow .item:not(:first-child) {
                margin-top: 30px;
            }
            @media(min-width: 768px) {
                .map {
                    height: 640px;
                }
            }
        `
    ];

    static properties = {
        isLoading: { type: Boolean, state: true }
    };

    constructor() {
        super();
        this.isLoading = true;
        this.map = null;
        this.infowindow = null;
        this.markerLayer = null;

        this.markerSVG = `<svg xmlns="http://www.w3.org/2000/svg" width="512" height="512" viewBox="0 0 512 512"><path fill="red" d="M256 0C160 0 82 78 82 174c0 101 80 164 123 239 42 75 42 102 51 102s9-27 51-99c43-75 123-138 123-239C430 78 352 0 256 0zm0 240c-37 0-67-30-67-67 0-37 30-67 67-67s67 30 67 67c0 37-30 67-37 67z"/><circle cx="256" cy="174" r="67" fill="rgba(0,0,0,0.01)"/></svg>`;
        this.markerSVGUrl = 'data:image/svg+xml;base64,' + btoa(this.markerSVG);
    }

    getCoords(fromLonLat) {
        const lon = parseFloat(this.dataset.lng);
        const lat = parseFloat(this.dataset.lat);

        if (isNaN(lon) || isNaN(lat)) {
            return null;
        }
        return fromLonLat([lon, lat]);
    }

    setupInfoWindow(Overlay) {
        const container = this.shadowRoot.getElementById('popup');

        if (this.dataset.title || this.dataset.content || this.dataset.imageurl) {
            const closer = this.shadowRoot.getElementById('popup-closer');

            this.infowindow = new Overlay({
                element: container,
                autoPanMargin: 150,
                offset: [0, -15],
                autoPan: true
            });
            this.map.addOverlay(this.infowindow);

            closer.onclick = (e) => {
                e.preventDefault();
                this.closePopup();
            };
        } else {
            container.style.display = "none";
        }
    }

    closePopup() {
        if (this.infowindow) {
            this.infowindow.setPosition(undefined);
        }
    }

    handleMapClick(source, feature) {
        if (feature) {
            const coordinates = feature.getGeometry().getCoordinates();
            this.infowindow?.setPosition(coordinates);
        } else {
            this.closePopup();
        }
    }

    async firstUpdated() {
        // Dynamically import OpenLayers modules concurrently
        const [
            { Map, View },
            { default: TileLayer },
            { default: OSM },
            { Style, Icon },
            { default: Point },
            { default: VectorSource },
            { default: VectorLayer },
            { defaults: defaultInteractions },
            { default: Feature },
            { fromLonLat },
            { default: Overlay },
            { default: FullScreen },
            { defaults: defaultControls }
        ] = await Promise.all([
            import('ol'),
            import('ol/layer/Tile'),
            import('ol/source/OSM'),
            import('ol/style'),
            import('ol/geom/Point'),
            import('ol/source/Vector'),
            import('ol/layer/Vector'),
            import('ol/interaction'),
            import('ol/Feature'),
            import('ol/proj'),
            import('ol/Overlay'),
            import('ol/control/FullScreen'),
            import('ol/control/defaults')
        ]);

        const coords = this.getCoords(fromLonLat);
        const zoom = parseFloat(this.dataset.zoom) || 12;

        if (coords) {
            const mapContainer = this.shadowRoot.querySelector('.map');

            this.map = new Map({
                target: mapContainer,
                interactions: defaultInteractions({ mouseWheelZoom: false }),
                controls: defaultControls().extend([new FullScreen()]),
                layers: [
                    new TileLayer({ source: new OSM() })
                ],
                view: new View({ center: coords, zoom })
            });

            // Marker styling and setup
            const markerStyle = new Style({
                image: new Icon({
                    anchor: [0.5, 1],
                    anchorXUnits: 'fraction',
                    anchorYUnits: 'fraction',
                    src: this.markerSVGUrl,
                    scale: 0.08
                })
            });

            const marker = new Feature({ geometry: new Point(coords) });
            marker.setStyle(markerStyle);

            this.markerLayer = new VectorLayer({
                source: new VectorSource({ features: [marker] }),
                renderOptions: { willReadFrequently: true }
            });

            this.map.addLayer(this.markerLayer);
            this.setupInfoWindow(Overlay);

            this.map.on('singleclick', (evt) => {
                const feature = this.map.forEachFeatureAtPixel(evt.pixel, (f) => f);
                const source = this.markerLayer.getSource();
                this.handleMapClick(source, feature);
            });

            if (this.dataset.title) {
                let pmTimer = null;

                this.map.on('pointermove', (evt) => {
                    if (pmTimer) return;

                    pmTimer = setTimeout(() => {
                        const tooltipElement = this.shadowRoot.getElementById('tooltip');
                        const features = this.map.getFeaturesAtPixel(evt.pixel);

                        if (features && features.length) {
                            tooltipElement.innerHTML = this.dataset.title;
                            tooltipElement.style.display = 'block';
                            tooltipElement.style.left = (evt.pixel[0] + 10) + 'px';
                            tooltipElement.style.top = (evt.pixel[1] + 10) + 'px';
                        } else {
                            tooltipElement.style.display = 'none';
                        }

                        const hit = this.map.hasFeatureAtPixel(evt.pixel, {
                            layerFilter: (layer) => layer === this.markerLayer
                        });

                        const mapTarget = this.map.getTargetElement();
                        if (mapTarget) {
                            mapTarget.style.cursor = hit ? 'pointer' : '';
                        }

                        pmTimer = null;
                    }, 100);
                });
            }

            this.isLoading = false;
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        // Prevent memory leaks when element is removed from DOM
        if (this.map) {
            this.map.setTarget(null);
            this.map = null;
        }
    }

    render() {
        const height = this.dataset.height ? `${this.dataset.height}px` : '100%';
        const imgHtml = this.dataset.imageurl
            ? html`<div class="img"><img src="${this.dataset.imageurl}" alt="${this.dataset.title || ''}" /></div>`
            : '';
        const contentHtml = this.dataset.content
            ? html`<div class="content">${unsafeHTML(this.dataset.content)}</div>`
            : '';
        const linkHtml = this.dataset.includelink === "true"
            ? html`<div class="link"><a href="https://www.google.com/maps/search/?api=1&query=${this.dataset.lat}%2C${this.dataset.lng}" target="_blank" rel="noopener noreferrer">View in Google Maps</a></div>`
            : '';

        return html`
            <div class="map" style="${this.isLoading ? 'display: none;' : 'display: block;'} height: ${height};"></div>
            <div id="tooltip" class="tooltip"></div>
            <div id="popup" class="ol-popup infomarker">
                <a href="#" id="popup-closer" class="ol-popup-closer">✖</a>
                <div id="popup-content">
                    <div class="title">${this.dataset.title}</div>
                    ${imgHtml}
                    ${contentHtml}
                    ${linkHtml}
                </div>
            </div>
        `;
    }
}

customElements.define('osm-map', OSMMap);